/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2019-2025 (original work) Open Assessment Technologies SA
 *
 */

import { match, compile } from 'path-to-regexp';
import qs from 'qs';
import { log } from '../core/utils/logger';
import { Userpilot } from 'userpilot';

/**
 * Global router using the history API to dispatch the URL to mapped routes.
 *
 * Routes are defined in config.
 * Controllers must be written, as a string parameter of the import,
 * due to webpack's static parsing of import()
 *
 */
const router = {
    /**
     * Table which consists all the possible routes in our app.
     * @type {import('./routingTable').Route[]}
     */
    routingTable: null,

    /**
     * Contains active controller instance
     */
    activeController: null,

    /**
     * An array of functions to be executed before route will be changed
     * @type Array<Promise<Boolean>>
     */
    beforeRouteChangeFunctions: [],
    /**
     * An array of functions to be executed after route was be changed
     * @type Array<Promise<any>>
     */
    afterRouteChangedFunctions: [],

    /**
     * Dispatch the router based on a URL.
     * The path will be used for the route and the parameters will be extracted.
     *
     * @see https://developer.mozilla.org/en-US/docs/Web/API/URL/URL
     * @param {string} url - the URL to dispatch
     * @param {string} [base] - the base URL
     * @throws {TypeError} if the URL is not correct (DOM requirements)
     * @returns {Promise}
     */
    async dispatchURL(url, base) {
        try {
            const parsedUrl = new URL(url, base || window.location.href);
            const parameters = qs.parse(parsedUrl.search.substring(1));
            const result = await this.dispatch(parsedUrl.pathname, parameters);
            if (result) {
                setTimeout(async () => {
                    Userpilot.reload();
                    await Promise.all(this.afterRouteChangedFunctions.map(fn => fn()));
                    this.afterRouteChangedFunctions = [];
                }, 0);
            }
            return result;
        } catch (err) {
            throw new TypeError(`Invalid URL to dispatch :'${url}' (${err.message})`);
        }
    },

    /**
     * Dispatch the given route
     * @param {string} path - an internal route
     * @param {Object} parameters - parameters to be given to the controller
     * @returns {Promise}
     */
    async dispatch(path, parameters = {}) {
        const couldProceed = async () => {
            const result = await Promise.all(this.beforeRouteChangeFunctions.map(fn => fn(path)));

            return !result.includes(false);
        };

        if (!(await couldProceed())) {
            return;
        }

        const currentRoute = this.routingTable.find(route => {
            if (match(route.path)(path)) {
                return route;
            }
        });

        const next = async () =>
            currentRoute.controller().then(async ({ default: controllerFactory }) => {
                if (typeof controllerFactory === 'function') {
                    const pathParameters = match(currentRoute.path)(path).params;
                    await this.startController(controllerFactory(), parameters, pathParameters);

                    return true;
                }

                return false;
            });

        this.beforeRouteChangeFunctions = [];

        if (typeof currentRoute.guard === 'function') {
            return currentRoute.guard(next, this);
        }

        return next();
    },

    /**
     * Prepare and start the provided controller
     * @param {PageController} controller
     * @param {Object} [parameters] - parameters to be given to the controller
     * @param {Object} [pathParameters] - parameters to be given to the controller
     * @returns {Promise}
     */
    async startController(controller, parameters = {}, pathParameters = {}) {
        if (this.activeController) {
            await this.stopActiveController();
        }

        for (let pathParameter in pathParameters) {
            if (pathParameters[pathParameter]) {
                pathParameters[pathParameter] = decodeURIComponent(pathParameters[pathParameter]);
            }
        }

        this.activeController = controller;
        controller.prepare();
        return controller.start(parameters, pathParameters);
    },

    /**
     * Stop controller and clean container
     */
    async stopActiveController() {
        await this.activeController.destroy();
        await this.activeController.destroyAdditionalComponents();
        this.activeController.clean();
        this.activeController = null;
    },

    /**
     * An auxiliary method for extracting url from state
     * @param {{ [url]: string, [fullUrl]: string}|string} state
     * @returns {{fullUrl: string, historyState: {url: string}}}
     */
    formatHistoryParams(state) {
        let fullUrl = '';
        let historyState = state;

        if (typeof state === 'string') {
            let url = state;
            historyState = { url };
            fullUrl = `${window.location.origin}${url}`;
        } else if (state.fullUrl) {
            fullUrl = state.fullUrl;
        } else if (state.url) {
            fullUrl = `${window.location.origin}${state.url}`;
        }
        return { historyState, fullUrl };
    },

    /**
     * Replace state
     * @param {object|string} state - History state object or URL
     * @param {boolean} dispatch - if need to dispatch the URL
     * @returns {string|undefined}
     */
    replace(state, dispatch = true) {
        const { historyState, fullUrl } = this.formatHistoryParams(state);

        window.history.replaceState(historyState, fullUrl.title, fullUrl);

        if (dispatch) {
            return this.dispatchURL(fullUrl);
        }
    },

    /**
     * @param {string} url
     */
    navigateOut(url) {
        window.location.href = url;
    },

    /**
     * @param {string} url
     */
    redirectOut(url) {
        window.location.replace(url);
    },

    /**
     * Push state
     * @param {{ [fullUrl]: string, [url]: string, [title]: string }|string} state - History state object or URL
     * @returns {Promise<string>}
     */
    async redirect(state) {
        const uid = Math.random();
        const { historyState, fullUrl } = this.formatHistoryParams(state);

        this.lastRedirectUid = uid;
        const result = await this.dispatchURL(fullUrl);
        // if a new redirect was called before the previous one was resolved, do not push state
        if (result && this.lastRedirectUid === uid) {
            window.history.pushState(historyState, historyState.title, fullUrl);
        }
        return result;
    },
    /**
     * @param {string} routeName Route name to redirect to.
     * @param {Object} [params] Route path params
     * @param {Object} [query] Route query
     * @param {String} [hash] Url hash
     * @returns {Promise<string>}
     */
    redirectToRoute(routeName, params, query, hash) {
        return this.redirect(this.compile(routeName, params, query, hash));
    },
    /**
     * History pop state event handler
     */
    onPopState() {
        router.dispatchURL(window.location.href);
    },

    /**
     * Entry point of the router that will start event listeners and check actual route
     * @param {import('./routingTable').Route[]} routingTable - returns a controller
     * @param {string} [url]
     * @returns {Promise}
     */
    start(routingTable, url = window.location.href) {
        this.routingTable = routingTable;
        //back & forward button, and push state
        window.addEventListener('popstate', this.onPopState);

        return this.dispatchURL(url);
    },

    /**
     * Stop router event listeners
     */
    stop() {
        window.removeEventListener('popstate', this.onPopState);
    },

    back() {
        window.history.back();
    },

    /**
     * Compile url with parameters
     * @param {string} url
     * @param {Object} [params]
     * @param {Object} [query]
     * @param {string} [hash]
     * @returns {string}
     */
    compile(url, params, query, hash) {
        for (let param in params ?? {}) {
            if (params[param]) {
                params[param] = encodeURIComponent(params[param]);
            }
        }
        let result = compile(url)(params);
        if (query) {
            result += `?${qs.stringify(query)}`;
        }
        if (hash) {
            result += `#${hash}`;
        }
        return result;
    },

    /**
     * @param {string} relativePath
     * @returns {string}
     */
    getFullPath(relativePath) {
        return new URL(relativePath, window.location.origin).href;
    },

    addBeforeRouteChangeFunction(fn) {
        if (typeof fn === 'function') {
            this.beforeRouteChangeFunctions.push(fn);

            // Return a function to remove the added function
            return () => {
                const index = this.beforeRouteChangeFunctions.indexOf(fn);
                if (index > -1) {
                    this.beforeRouteChangeFunctions.splice(index, 1);
                }
            };
        } else {
            log.warn('Invalid beforeRouteChange function provided');
            return () => {}; // Return an empty function to avoid breaking the calling code
        }
    },
    addAfterRouteChangedFunction(fn) {
        if (typeof fn === 'function') {
            this.afterRouteChangedFunctions.push(fn);

            // Return a function to remove the added function
            return () => {
                const index = this.afterRouteChangedFunctions.indexOf(fn);
                if (index > -1) {
                    this.afterRouteChangedFunctions.splice(index, 1);
                }
            };
        } else {
            log.warn('Invalid beforeRouteChange function provided');
            return () => {}; // Return an empty function to avoid breaking the calling code
        }
    },
    clearBeforeRouteChangeFunction() {
        this.beforeRouteChangeFunctions = [];
    }
};

export default router;
